// ── Phase-1 wallet-signature auth (CLIENT half). ──
// The player signs ONE message after connecting (a single Phantom popup) to prove wallet
// ownership and receives a session token that is then attached to every API request + the
// realtime join. This is BEST-EFFORT and COMPATIBLE: if no injected provider can sign (a
// URL-handoff / mobile player) or the user declines, we silently stay tokenless and the game
// behaves exactly as before — the Phase-1 server still accepts unauthenticated requests.
// NOTHING here ever throws or blocks world entry.

import { signingProvider } from './wallet.js';

const API_URL: string | undefined = import.meta.env.VITE_API_URL;
const TOKEN_STORAGE_KEY = 'mn_session_token';

/** In-memory token cache (mirrors localStorage). Read by api.ts + client.ts on every request. */
let sessionToken: string | null = null;
/** The wallet the cached token belongs to, so we never reuse a token for a different wallet. */
let tokenWallet: string | null = null;
/** Dedupe: avoid firing the sign-in popup twice for the same wallet in one session. */
let signInInFlight: Promise<void> | null = null;

function loadStored(): { token: string; wallet: string } | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(TOKEN_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { token?: unknown; wallet?: unknown };
    if (typeof parsed.token === 'string' && typeof parsed.wallet === 'string') {
      return { token: parsed.token, wallet: parsed.wallet };
    }
  } catch {
    /* ignore malformed / private mode */
  }
  return null;
}

function persist(token: string, wallet: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify({ token, wallet }));
  } catch {
    /* ignore */
  }
}

/** Base64-encode raw signature bytes (browser-safe, no Buffer dependency). */
function toBase64(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary);
}

/**
 * The current session token IF it belongs to `wallet` (when given). Returns the in-memory
 * value, falling back to a matching persisted token so a refresh reuses it. Null when absent /
 * for a different wallet. Pure read — never prompts.
 * @param {string | null} [wallet]
 */
export function getSessionToken(wallet?: string | null): string | null {
  if (sessionToken && (!wallet || tokenWallet === wallet)) return sessionToken;
  const stored = loadStored();
  if (stored && (!wallet || stored.wallet === wallet)) {
    sessionToken = stored.token;
    tokenWallet = stored.wallet;
    return sessionToken;
  }
  return null;
}

/** Forget the session token (in memory + storage). Called on wallet disconnect. */
export function clearSessionToken(): void {
  sessionToken = null;
  tokenWallet = null;
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(TOKEN_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Prove ownership of `wallet` and store a session token. Best-effort:
 *   - no API configured, no signing provider, user declines, or any network/HTTP failure → no-op,
 *     and we stay tokenless (the game continues exactly as today).
 *   - already have a valid token for this wallet → no popup, returns immediately.
 * NEVER throws. Safe to call right after the wallet is resolved without blocking world entry.
 * @param {string | null} wallet
 * @returns {Promise<void>}
 */
export function signInWallet(wallet: string | null): Promise<void> {
  if (!wallet) return Promise.resolve();
  if (typeof API_URL !== 'string' || API_URL.length === 0) return Promise.resolve();
  // Reuse a still-valid token for this wallet — no second popup.
  if (getSessionToken(wallet)) return Promise.resolve();
  // Only attempt when an injected provider can actually sign (URL-handoff players have none).
  const provider = signingProvider();
  if (!provider || typeof provider.signMessage !== 'function') return Promise.resolve();

  if (signInInFlight) return signInInFlight;
  signInInFlight = (async () => {
    try {
      // 1. nonce
      const nonceRes = await fetch(`${API_URL}/auth/nonce?wallet=${encodeURIComponent(wallet)}`, {
        method: 'GET',
        headers: { accept: 'application/json' },
        signal: AbortSignal.timeout(8000),
      });
      if (!nonceRes.ok) return;
      const { message } = (await nonceRes.json().catch(() => ({}))) as { message?: string };
      if (typeof message !== 'string' || !message) return;

      // 2. sign (this is the single Phantom popup — the user may decline → caught below)
      const enc = new TextEncoder().encode(message);
      const { signature } = await provider.signMessage(enc, 'utf8');
      if (!(signature instanceof Uint8Array) || signature.length === 0) return;

      // 3. verify → token
      const verifyRes = await fetch(`${API_URL}/auth/verify`, {
        method: 'POST',
        headers: { 'content-type': 'application/json', accept: 'application/json' },
        body: JSON.stringify({ wallet, message, signature: toBase64(signature) }),
        signal: AbortSignal.timeout(8000),
      });
      if (!verifyRes.ok) return;
      const { token } = (await verifyRes.json().catch(() => ({}))) as { token?: string };
      if (typeof token !== 'string' || !token) return;

      sessionToken = token;
      tokenWallet = wallet;
      persist(token, wallet);
    } catch {
      // No provider, user declined, network error, timeout, malformed — all non-fatal.
      // We simply remain tokenless and the Phase-1 server accepts the request anyway.
    } finally {
      signInInFlight = null;
    }
  })();
  return signInInFlight;
}
